// 完了画面のログインポップアップ計測のうち、Vue に依存しない定義とストレージ操作。
// 状態と購読は composables/format-edit/useLoginPopupTracking.ts 側に置く。
//
// 参照元は2つ:
//   - composables/format-edit/useCompletedOutput.ts（ポップアップ表示・放棄・同一ページでのログイン成功）
//   - services/firebase/authLifecycle.ts（リダイレクト系ログインの復帰処理）

// Google/AppleID/LINEのログインは signInWithRedirect / 外部URL遷移で一度ページを離れるため、
// setup が保持するメモリ上の試行状態はページ再読み込みで失われ、戻ってきても
// login_popup_login_success を送信できない。そこでポップアップを開いた時点でこのキーを使って
// 計測コンテキストを永続ストレージ（localforage）に退避し、リダイレクト復帰後の認証確定時に
// authLifecycle.ts 側で回収して成功イベントを送信する。
export const LOGIN_POPUP_REDIRECT_KEY = 'loginPopupRedirectTracking'

/**
 * 放棄を計上した理由。
 *
 * - dialogClose … ダイアログを明示的に閉じた（従来からの計上経路）
 * - pageHidden … ページが hidden になった（タブ切替・離脱・リダイレクト系ログインの遷移）
 *
 * pageHidden は「戻ってこないユーザー」を取りこぼさないために hidden の時点で計上する。
 * 戻ってきてログインまで到達した分は login_popup_abandon_recovered で打ち消せるので、
 * 集計側では abandon − abandon_recovered が真の放棄になる。
 */
export const LOGIN_POPUP_ABANDON_REASON = {
  dialogClose: 'dialog_close',
  pageHidden: 'page_hidden'
} as const

export type LoginPopupAbandonReason =
  (typeof LOGIN_POPUP_ABANDON_REASON)[keyof typeof LOGIN_POPUP_ABANDON_REASON]

/** localforage に退避する計測コンテキスト。 */
export type LoginPopupRedirectRecord = {
  triggerButton: string
  formatId: string
  /** ポップアップを開いた時刻。TTL 判定に使う */
  ts: number
}

// hidden で放棄を計上した事実を残すキー。
//
// ⚠️ ここだけ localforage ではなく localStorage を使う。hidden の直後にリダイレクトで
// ページが破棄されるため、IndexedDB への非同期書き込みでは間に合う保証がない。
// 打ち消しの根拠が落ちると、復帰してログインした分まで放棄に残って過大計上になる。
// 同期書き込みが必要な計測フラグを localStorage に置くのは gtm-datalayer.js の
// 認証ヒント（_yagish_auth_hint）と同じ方針。
const ABANDON_FLAG_KEY = '_yagish_login_popup_abandoned'

// 放棄から成功までをひと続きの試行とみなす上限。リダイレクト側の
// LOGIN_POPUP_REDIRECT_TTL_MS と揃える（これを超えたら別の来訪として扱う）。
const ABANDON_FLAG_TTL_MS = 30 * 60 * 1000

// プライベートブラウジング等で localStorage が使えない環境でも、計測のために
// 本体機能を落とさない。読めない場合は「放棄していない」に倒す（打ち消しを送らない側）。
function safeGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch (e) {
    return null
  }
}

function safeSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value)
  } catch (e) {}
}

function safeRemove(key: string): void {
  try {
    window.localStorage.removeItem(key)
  } catch (e) {}
}

/** hidden による放棄を計上したことを記録する。リダイレクトを跨いで読めるよう同期で書く。 */
export function markLoginPopupAbandoned(reason: LoginPopupAbandonReason): void {
  safeSet(ABANDON_FLAG_KEY, JSON.stringify({ reason, at: Date.now() }))
}

/** 記録を消す。新しい試行を始めるときに呼び、前の試行の放棄を持ち越さない。 */
export function clearLoginPopupAbandoned(): void {
  safeRemove(ABANDON_FLAG_KEY)
}

/**
 * 放棄の記録を読んで消す。返り値があれば、その放棄はログイン成功で打ち消せる。
 * 期限切れ・壊れているものは破棄して null を返す。
 */
export function consumeLoginPopupAbandoned(): LoginPopupAbandonReason | null {
  const raw = safeGet(ABANDON_FLAG_KEY)
  if (!raw) {
    return null
  }
  safeRemove(ABANDON_FLAG_KEY)
  let parsed: { reason?: string; at?: number } | null = null
  try {
    parsed = JSON.parse(raw)
  } catch (e) {
    return null
  }
  if (!parsed || typeof parsed.at !== 'number' || !parsed.reason) {
    return null
  }
  if (Date.now() - parsed.at > ABANDON_FLAG_TTL_MS) {
    return null
  }
  return parsed.reason as LoginPopupAbandonReason
}
